import { useMemo } from 'react';

import { AdditionalEntitlement, MedicinePrescription } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { Reimbursement } from '~libs.shared.prescription';
import { MedicinePackage } from '~libs.desktop.shared.medicine';

import { draftPrescriptionsTranslationKeys } from '../../../../../translations';
import { useReimbursementSelector } from '../../../reimbursement/reimbursement.hooks';
import {
   useAdditionalEntitlementsSelector,
   useSelectedAdditionalEntitlementsSync,
} from '../../prescription/additionalEntitlements/additionalEntitlements.hooks';
import { useAdditionalRequirementsSelector } from '../../prescription/additionalRequirements/additionalRequirements.hooks';
import { MedicineHeading } from '../../prescription/MedicineHeading';
import { PrescriptionCard } from '../../prescription/PrescriptionCard';
import { DraftPrescriptionRemoveIcon } from '../common/DraftPrescriptionRemoveIcon';
import { DraftReimbursementSelector } from '../common/DraftReimbursementSelector';
import { DraftPrescriptionIndex, DraftPrescriptionsFormController } from '../DraftPrescriptionsForm.types';

import {
   useDosageFrequencyNotAllowed,
   useDosageValidationSync,
   useIssuedForYear,
   useNumericalDosageValidationSync,
   useTreatmentDaysCountState,
} from './DraftStandardPrescriptionField.hooks';
import {
   useExtendedDosageComputedValuesSync,
   useExtendedDosageState,
   useWatchedExtendedDosage,
} from './DraftStandardPrescriptionExtendedDosage.hooks';
import { DraftStandardPrescription } from './DraftStandardPrescriptionField.types';
import { createDraftStandardPrescriptionsFieldNameFactory } from './DraftStandardPrescriptionField.utils';
import { DraftStandardPrescriptionFieldGrid } from './DraftStandardPrescriptionFieldGrid';
import { DraftStandardPrescriptionStatuses } from './DraftStandardPrescriptionStatuses';

type DraftStandardPrescriptionFieldProps = DraftStandardPrescription &
   Pick<
      DraftPrescriptionsFormController,
      'register' | 'watch' | 'setValue' | 'getValue' | 'trigger' | 'formState' | 'setError'
   > & {
      readonly index: DraftPrescriptionIndex;
      readonly onRemove: VoidFunction;
      readonly medicinePrescription: MedicinePrescription;
      readonly visitDiagnoses: VisitDiagnosis[];
      readonly inPersonExaminationPerformed: boolean;
   };

// eslint-disable-next-line max-lines-per-function,max-statements
export const DraftStandardPrescriptionField = (props: DraftStandardPrescriptionFieldProps): JSX.Element => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(props.index);
   const patientMedicine = props.medicinePrescription.patientMedicine!;

   const computedAdditionalEntitlements = useAdditionalEntitlementsSelector({
      initialAdditionalEntitlementTypes: props.initialData.additionalEntitlementTypes,
      selectedPatientReimbursementByUser: props.selectedByUser.patientReimbursement,
      medicinePrescription: props.medicinePrescription,
   });
   const computedReimbursement = useReimbursementSelector({
      initialReimbursementType: props.initialData.reimbursementType,
      selectedPatientReimbursementByUser: props.selectedByUser.patientReimbursement,
      additionalEntitlements: computedAdditionalEntitlements,
      reimbursements: props.medicinePrescription.reimbursements,
   });
   const computedAdditionalRequirements = useAdditionalRequirementsSelector({
      initialAdditionalRequirementTypes: props.initialData.additionalRequirementsTypes,
   });

   const issuedForYear = useIssuedForYear({
      index: props.index,
      watch: props.watch,
      additionalRequirementsTypes: props.initialData.additionalRequirementsTypes,
   });

   const currentReimbursement: Reimbursement | null | undefined =
      props.watch(createFieldName('reimbursement')) ?? computedReimbursement;

   const currentExtendedDosage = useWatchedExtendedDosage({
      watched: props.watch(createFieldName('extendedDosage')),
      fallback: props.data.extendedDosage,
   });

   const extendedDosage = useExtendedDosageState({
      medicine: patientMedicine.medicine,
      extendedDosage: currentExtendedDosage,
      issuedForYear: issuedForYear,
   });

   useExtendedDosageComputedValuesSync({
      setValue: props.setValue,
      index: props.index,
      extendedDosage: extendedDosage,
      initialQuantity: props.initialData.quantity,
   });

   const treatmentDaysCount = useTreatmentDaysCountState({
      watch: props.watch,
      setValue: props.setValue,
      index: props.index,
      medicine: patientMedicine.medicine,
      issuedForYear: issuedForYear,
      reimbursement: currentReimbursement,
      extendedDosageActive: extendedDosage.active,
   });

   const { underTreatmentSupervision } = treatmentDaysCount;

   const dosageFrequency = useDosageFrequencyNotAllowed({
      watch: props.watch,
      index: props.index,
      issuedForYear: issuedForYear,
      reimbursement: currentReimbursement,
      extendedDosage: extendedDosage.active ? currentExtendedDosage : null,
   });

   useNumericalDosageValidationSync({
      trigger: props.trigger,
      index: props.index,
      underTreatmentSupervision: underTreatmentSupervision,
   });

   const dosageFieldRequired = useDosageValidationSync({
      watch: props.watch,
      trigger: props.trigger,
      index: props.index,
      underTreatmentSupervision: underTreatmentSupervision,
      extendedDosageActive: extendedDosage.active,
   });

   const additionalEntitlementsFieldName = createFieldName('additionalEntitlements');
   const currentSelectedEntitlements = props.watch<
      typeof additionalEntitlementsFieldName,
      AdditionalEntitlement[] | undefined
   >(additionalEntitlementsFieldName);

   const selectedEntitlements = useMemo(() => currentSelectedEntitlements ?? computedAdditionalEntitlements, [
      computedAdditionalEntitlements,
      currentSelectedEntitlements,
   ]);

   useSelectedAdditionalEntitlementsSync(
      props.medicinePrescription,
      selectedEntitlements,
      props.setValue,
      createFieldName('additionalEntitlements')
   );

   const activeIngredientsQuantity: number | null =
      extendedDosage.active && !extendedDosage.packagesCountEditable
         ? extendedDosage.packagesCount ?? null
         : props.watch<ReturnType<typeof createFieldName>, number | null>(createFieldName('quantity'));

   const medicineSufficiencyCount = extendedDosage.active
      ? extendedDosage.administrationDaysCount
      : treatmentDaysCount.medicineSufficiencyCount;

   return (
      <PrescriptionCard
         testId={props.index}
         heading={
            <MedicineHeading
               medicine={patientMedicine.medicine}
               reimbursements={patientMedicine.reimbursements}
               onConfirm={props.onRemove}
               confirmationButtonLabel={draftPrescriptionsTranslationKeys.removeMedicineFromPrescription}
               visitDiagnoses={props.visitDiagnoses}
            />
         }
         subheading={
            <MedicinePackage
               dose={patientMedicine.medicine.dose}
               form={patientMedicine.medicine.form}
               packageContent={patientMedicine.medicine.packageContent}
            />
         }
         options={<DraftPrescriptionRemoveIcon onRemove={props.onRemove} />}
      >
         <DraftStandardPrescriptionStatuses
            previouslyUsedMedicineId={patientMedicine.previouslyUsedMedicineId}
            medicine={patientMedicine.medicine}
            inPersonExaminationPerformed={props.inPersonExaminationPerformed}
            ruleVerificationResults={props.initialData.ruleValidationResults}
         />

         <DraftReimbursementSelector
            register={props.register}
            name={createFieldName('reimbursement')}
            defaultValue={computedReimbursement}
            reimbursements={props.medicinePrescription.reimbursements}
            visitDiagnoses={props.visitDiagnoses}
            selectedEntitlements={selectedEntitlements}
         />
         <DraftStandardPrescriptionFieldGrid
            register={props.register}
            trigger={props.trigger}
            setValue={props.setValue}
            setError={props.setError}
            formState={props.formState}
            watch={props.watch}
            index={props.index}
            data={props.data}
            extendedDosageValue={currentExtendedDosage}
            initialQuantity={props.initialData.quantity}
            medicinePrescription={props.medicinePrescription}
            medicine={patientMedicine.medicine}
            visitDiagnoses={props.visitDiagnoses}
            extendedDosage={extendedDosage}
            treatmentDaysCount={treatmentDaysCount}
            underTreatmentSupervision={underTreatmentSupervision}
            dosageFieldRequired={dosageFieldRequired}
            dosageFrequencyNotAllowed={dosageFrequency.notAllowed}
            dosageFrequencyValidated={dosageFrequency.validated}
            computedAdditionalEntitlements={computedAdditionalEntitlements}
            computedAdditionalRequirements={computedAdditionalRequirements}
            activeIngredientsQuantity={activeIngredientsQuantity}
            medicineSufficiencyCount={medicineSufficiencyCount}
         />
      </PrescriptionCard>
   );
};
